// Single source of truth for the JWT issued by https://www.ki-wealth.com.
//
// The token is the user's Forge Ultra session, so we deliberately namespace
// the storage key as "forge_token" (and intentionally do NOT use the legacy
// "wp_jwt_token" key any longer — see login-success.tsx for the migration).
//
// We use localStorage rather than cookies because the FastAPI backend expects
// the token in the Authorization header (Bearer scheme) — there is no
// cookie-based session on the API side. The 401 response interceptor in
// api/client.ts is responsible for clearing this key and bouncing the user
// back to the WP login page when the token expires.

export const TOKEN_STORAGE_KEY = 'forge_token';

// Legacy keys we still want to read once and migrate, so users who already
// landed on /login-success while we were storing the token under a different
// name don't get logged out by the rename.
const LEGACY_TOKEN_KEYS = ['wp_jwt_token'] as const;

export function getToken(): string | null {
    if (typeof window === 'undefined') return null;

    const current = window.localStorage.getItem(TOKEN_STORAGE_KEY);
    if (current) return current;

    // One-shot migration: copy the first legacy value we find into the
    // canonical key, then forget the legacy one.
    for (const legacy of LEGACY_TOKEN_KEYS) {
        const value = window.localStorage.getItem(legacy);
        if (value) {
            window.localStorage.setItem(TOKEN_STORAGE_KEY, value);
            window.localStorage.removeItem(legacy);
            return value;
        }
    }

    return null;
}

export function setToken(token: string): void {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
}

export function clearToken(): void {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(TOKEN_STORAGE_KEY);
    for (const legacy of LEGACY_TOKEN_KEYS) {
        window.localStorage.removeItem(legacy);
    }
}

// --- Login-bounce loop protection ---------------------------------------
// When the SPA receives a 401 we send the user to the WordPress login URL
// so they can re-authenticate. WP, however, does not always issue a fresh
// JWT for visitors who are already logged in (the `pmpro_login_redirect_url`
// filter only fires on a real login submission). If WP just bounces them
// back to /dashboard without a token, the SPA would 401 again and bounce
// again — producing the visible "blinking" redirect loop reported in
// production.
//
// We mitigate this with a sessionStorage marker: we record the timestamp
// of every outbound bounce, and if we land back on a protected route
// within LOGIN_BOUNCE_WINDOW_MS without a token, we suppress the next
// redirect and let the caller render a manual sign-in CTA instead.
const LOGIN_BOUNCE_KEY = 'forge_login_bounce_at';
const LOGIN_BOUNCE_WINDOW_MS = 15_000;

function readBounceTimestamp(): number {
    if (typeof window === 'undefined') return 0;
    const raw = window.sessionStorage.getItem(LOGIN_BOUNCE_KEY);
    const ts = Number(raw);
    return Number.isFinite(ts) ? ts : 0;
}

// Returns true iff a login bounce was attempted within the protection
// window — i.e. the caller is currently inside a potential loop and
// should NOT redirect again.
export function isLoginBounceLoop(): boolean {
    return Date.now() - readBounceTimestamp() < LOGIN_BOUNCE_WINDOW_MS;
}

// Attempt to redirect to the WordPress login page. Returns true if a
// redirect was issued, false if it was suppressed because we already
// bounced very recently (loop guard).
//
// The actual ``window.location.href`` assignment is deferred to the next
// macrotask so that any promise rejection currently in flight (e.g. the
// axios 401 response interceptor in ``api/client.ts``) can settle FIRST.
// Navigating synchronously from inside a rejection handler tears the page
// down before its ``.catch`` / ``finally`` chains run and before Chrome's
// message-channel listeners (extensions, devtools, service workers) have
// delivered their async responses, surfacing as:
//
//     Uncaught (in promise) Error: A listener indicated an asynchronous
//     response by returning true, but the message channel closed before
//     a response was received.
//
// The loop-guard state (sessionStorage timestamp) is still written
// synchronously so a second 401 arriving before the macrotask fires sees
// ``isLoginBounceLoop() === true`` and is correctly suppressed.
export function bounceToLoginOnce(loginUrl: string): boolean {
    if (typeof window === 'undefined') return false;
    if (isLoginBounceLoop()) return false;
    window.sessionStorage.setItem(LOGIN_BOUNCE_KEY, String(Date.now()));
    window.setTimeout(() => {
        window.location.href = loginUrl;
    }, 0);
    return true;
}

// Called after a successful auth so the next legitimate session-expiry
// still triggers a redirect (the marker would otherwise persist for the
// rest of the tab's lifetime).
export function clearLoginBounce(): void {
    if (typeof window === 'undefined') return;
    window.sessionStorage.removeItem(LOGIN_BOUNCE_KEY);
}
